/**
 * Packages and switchable features.
 *
 * Every extra is a feature: the workshop's package decides whether it is
 * available, the workshop decides whether it is on (off by default, except the few the owner
 * decided to have on from the start: `defaultOn`; the workshop can still switch them off).
 * Each feature needs `feature.<key>.label`, `feature.<key>.description` (one line) and
 * `feature.<key>.details` ("What is it for?": daily use, an example, what it costs, the package
 * as {{plan}}, what changes when off; paid add-ons also {{price}}) in every locale, and a row in the
 * database table public.features
 * (a migration; a database test keeps the two lists equal).
 */
export type Plan = "basic" | "medium" | "premium";
export const PLANS: readonly Plan[] = ["basic", "medium", "premium"];

export type FeatureGroup = "workOrder" | "customer" | "stock" | "money" | "ai" | "extra";
export const FEATURE_GROUPS: readonly FeatureGroup[] = ["workOrder", "customer", "stock", "money", "ai", "extra"];

export interface FeatureDefinition {
  key: string;
  group: FeatureGroup;
  /** Smallest package that has it. */
  plan: Plan;
  /** Features that must be on for this one to work. */
  requires?: readonly string[];
  /** On from the start for new workshops (owner's decision); still switchable. Keep equal to the
   * default of public.workshops.settings (a database test checks it). */
  defaultOn?: true;
  /** A paid add-on: what one unit costs the workshop (added to its monthly bill). */
  price?: { amount: number; currency: string; unit: "sms" };
}

/** What one SMS sent by Garagevo costs the workshop (forint; added to the monthly bill). */
export const SMS_PRICE_HUF = 25;

export const FEATURES: readonly FeatureDefinition[] = [
  // Stock keeping: receipts, stocktake, work order lines take parts out of stock (only warns).
  { key: "stock", group: "stock", plan: "medium" },
  // Quotes: lines marked for approval, the customer answers per line on a link (or by phone).
  { key: "quotes", group: "workOrder", plan: "medium" },
  // The program sends the quote link by SMS itself: a paid add-on in every package when
  // Garagevo sends it, free from the workshop's own Android phone (needs quotes).
  { key: "sms", group: "customer", plan: "basic", requires: ["quotes"], price: { amount: SMS_PRICE_HUF, currency: "HUF", unit: "sms" } },
  // E-mail to the customer listing the recommended work they declined (needs quotes).
  { key: "declinedWorkEmail", group: "customer", plan: "medium", requires: ["quotes"] },
  // AI: reading a registration certificate or data sheet photo into a new vehicle.
  { key: "aiVehiclePhoto", group: "ai", plan: "basic" },
  // AI: looks up the shared type data sheet of a type once (shared work, platform cost).
  { key: "aiDataSheet", group: "ai", plan: "basic" },
  // Technical inspection and service due reminders to the customers who asked for them, by e-mail
  // and/or SMS (SMS needs the "sms" feature); whether it is paid is decided later (2026-11-01).
  { key: "reminders", group: "customer", plan: "medium" },
  // Due services when the odometer is entered: the type data sheet's maintenance plan against the
  // vehicle's own closed work orders; only warns (owner's idea, built 2026-11-02).
  { key: "dueServices", group: "workOrder", plan: "basic", defaultOn: true },
  // Intake with photos: the guided round of the car (5 base views and damage close ups) when a work order is
  // opened, a collage on the intake receipt and a personal link for the customer (owner's design, 2026-11-04).
  { key: "intakePhotos", group: "workOrder", plan: "medium" },
  // The customer signs the intake receipt and the handover on the phone with a finger (separate switch).
  { key: "phoneSignature", group: "workOrder", plan: "medium" },
  // Extra modules
  // Download of every data of the workshop in one ZIP (backup, moving to another program).
  { key: "dataExport", group: "extra", plan: "basic" },
];

/** Features a new workshop starts with (each must be in the basic package). */
export const DEFAULT_ON_FEATURES: readonly string[] = FEATURES.filter((f) => f.defaultOn).map((f) => f.key);

export function planIncludes(plan: Plan, needed: Plan): boolean {
  return PLANS.indexOf(plan) >= PLANS.indexOf(needed);
}

export function feature(key: string): FeatureDefinition | undefined {
  return FEATURES.find((f) => f.key === key);
}

/** The package has this feature, or the operator allowed it beyond the package (`extra`). */
export function featureAvailable(plan: Plan, key: string, extra: readonly string[] = []): boolean {
  const f = feature(key);
  return !!f && (planIncludes(plan, f.plan) || extra.includes(key));
}

/** The feature works: in the package, switched on, and everything it needs is on too. */
export function featureOn(w: { plan: Plan; features: readonly string[]; extraFeatures?: readonly string[] }, key: string, seen: string[] = []): boolean {
  const f = feature(key);
  if (!f || seen.includes(key) || !featureAvailable(w.plan, key, w.extraFeatures) || !w.features.includes(key)) return false;
  return (f.requires ?? []).every((r) => featureOn(w, r, [...seen, key]));
}

/** Keeps only known features the package has or the operator allowed (used when saving and after a package change). */
export function cleanFeatures(plan: Plan, keys: unknown, extra: readonly string[] = []): string[] {
  if (!Array.isArray(keys)) return [];
  return [...new Set(keys.filter((k): k is string => typeof k === "string" && featureAvailable(plan, k, extra)))];
}

export function isPlan(v: unknown): v is Plan {
  return typeof v === "string" && (PLANS as readonly string[]).includes(v);
}
